import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import type {
  ProjectService,
  ProjectServicesPage,
  ServiceHealth,
  ServiceReleaseObservation,
  ServiceReleaseState,
  ServiceSample,
} from "@/lib/types";
import { Button } from "@/components/Button";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import type { Locale } from "@/locale";
import type { Messages } from "@/locale/types";
import { useLocale } from "@/locale/LocaleContext";
import ui from "@/components/ui.module.css";
import styles from "@/pages/app/services.module.css";

const STALE_AFTER_MS = 90_000;
const TICK_MS = 5_000;

function healthRail(health: ServiceHealth): string {
  if (health === "healthy") return "succeeded";
  if (health === "degraded") return "running";
  if (health === "stale") return "stale";
  if (health === "unavailable") return "failed";
  return "accepted";
}

function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

function formatNullable(
  value: number | null | undefined,
  locale: string,
  unknown: string,
  suffix = "",
): string {
  if (value == null || !Number.isFinite(value)) return unknown;
  return `${formatCount(value, locale)}${suffix}`;
}

function formatBool(value: boolean | null | undefined, yes: string, no: string, unknown: string): string {
  if (value === true) return yes;
  if (value === false) return no;
  return unknown;
}

/** Recompute stale client-side so UI updates after 90s without a successful fetch. */
function deriveHealth(service: ProjectService, nowMs: number): ServiceHealth {
  if (!service.latest) {
    return service.health === "unavailable" ? "unavailable" : "pending";
  }
  const observed = Date.parse(service.latest.observed_at);
  if (!Number.isFinite(observed)) {
    return service.health === "pending" ? "pending" : "stale";
  }
  if (nowMs - observed > STALE_AFTER_MS) return "stale";
  return service.health;
}

function scaleTicksForMax(max: number): number[] {
  if (max <= 1) return [max, 0];
  const mid = max / 2;
  if (mid === max || mid === 0) return [max, 0];
  return [max, mid, 0];
}

function formatScaleTick(value: number, locale: string): string {
  const fractionDigits = Number.isInteger(value) ? 0 : 1;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

function sampleTimeLabel(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZone: "UTC",
    hour12: false,
  }).format(d);
}

function sampleAxisLabel(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hour12: false,
  }).format(d);
}

/** First, last, and evenly spaced ticks — avoids dense 60-label clutter. */
function shouldShowAxisLabel(index: number, total: number): boolean {
  if (total <= 1) return true;
  if (index === 0 || index === total - 1) return true;
  const step = Math.max(1, Math.round((total - 1) / 4));
  return index % step === 0 && index !== total - 1;
}

function releaseStateLabel(m: Messages, state: ServiceReleaseState | string): string {
  if (state === "progressing") return m.projects.servicesReleaseProgressing;
  if (state === "available") return m.projects.servicesReleaseAvailable;
  if (state === "failed") return m.projects.servicesReleaseFailed;
  return m.projects.servicesUnknown;
}

function releaseDotClass(state: ServiceReleaseState | string): string {
  if (state === "progressing") return styles.releaseDotProgressing ?? "";
  if (state === "available") return styles.releaseDotAvailable ?? "";
  if (state === "failed") return styles.releaseDotFailed ?? "";
  return "";
}

type Props = {
  projectId: string;
};

export function ProjectServicesPanel({ projectId }: Props) {
  const { m, locale, fmt } = useLocale();
  const [page, setPage] = useState<ProjectServicesPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const projectIdRef = useRef(projectId);
  const failRef = useRef(m.projects.servicesFailed);
  projectIdRef.current = projectId;
  failRef.current = m.projects.servicesFailed;

  useEffect(() => {
    setPage(null);
    setError(null);
    setLoading(true);
  }, [projectId]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    const ac = new AbortController();
    const captured = projectId;
    setLoading(true);
    setError(null);
    api
      .projectServices(captured, ac.signal)
      .then((res) => {
        if (!alive || projectIdRef.current !== captured) return;
        setPage(res);
        setError(null);
        setNowMs(Date.now());
      })
      .catch((e) => {
        if (!alive || ac.signal.aborted) return;
        if (projectIdRef.current !== captured) return;
        setPage(null);
        setError(e instanceof Error ? e.message : failRef.current);
      })
      .finally(() => {
        if (alive && projectIdRef.current === captured) setLoading(false);
      });
    return () => {
      alive = false;
      ac.abort();
    };
  }, [projectId, retry]);

  const displayPage = page;
  const unknown = m.projects.servicesUnknown;

  return (
    <section className={ui.panel} style={{ marginTop: "1.25rem" }} aria-labelledby="project-apps">
      <h2 id="project-apps" className="display" style={{ margin: 0, fontSize: "1.1rem" }}>
        {m.projects.servicesTitle}
      </h2>
      <p className={ui.meta} style={{ marginTop: "0.35rem" }}>
        {m.projects.servicesLead}
      </p>

      <div className={styles.toolbar}>
        {displayPage ? (
          <p className={ui.meta} style={{ margin: 0 }}>
            {fmt(m.projects.servicesObserved, {
              when: formatWhen(displayPage.observed_at, locale),
            })}
          </p>
        ) : (
          <p className={ui.meta} style={{ margin: 0 }}>
            {m.projects.servicesSource}
          </p>
        )}
        <Button
          type="button"
          variant="secondary"
          disabled={loading}
          aria-busy={loading || undefined}
          onClick={() => setRetry((n) => n + 1)}
        >
          {loading ? m.projects.servicesRefreshing : m.projects.servicesRefresh}
        </Button>
      </div>

      {loading && !displayPage ? <StatePanel kind="pending" title={m.common.loading} /> : null}

      {error ? (
        <div role="alert" style={{ marginTop: "0.75rem" }}>
          <p className={ui.fieldError}>{error}</p>
          <Button type="button" variant="secondary" onClick={() => setRetry((n) => n + 1)}>
            {m.projects.servicesRetry}
          </Button>
        </div>
      ) : null}

      {displayPage && !error ? (
        <div aria-busy={loading || undefined}>
          <p className={ui.meta} style={{ marginTop: "0.65rem" }}>
            {m.projects.servicesCapsNote}
          </p>

          {displayPage.items.length === 0 ? (
            <StatePanel
              kind="empty"
              title={m.projects.servicesEmpty}
              message={m.projects.servicesEmptyBody}
            />
          ) : (
            displayPage.items.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                nowMs={nowMs}
                locale={locale}
                unknown={unknown}
              />
            ))
          )}
        </div>
      ) : null}
    </section>
  );
}

function ServiceCard({
  service,
  nowMs,
  locale,
  unknown,
}: {
  service: ProjectService;
  nowMs: number;
  locale: Locale;
  unknown: string;
}) {
  const { m, fmt } = useLocale();
  const chartId = useId();
  const releasesId = useId();
  const health = deriveHealth(service, nowMs);
  const latest = service.latest;
  // Older API builds may omit releases during deployment transition.
  const releases = service.releases ?? [];

  const durations = useMemo(
    () => service.series.map((s) => s.probe_duration_ms).filter((v): v is number => v != null && Number.isFinite(v)),
    [service.series],
  );
  const maxDuration = Math.max(1, ...durations);
  const scaleTicks = scaleTicksForMax(maxDuration);

  return (
    <article className={styles.card}>
      <div className={styles.cardHead}>
        <div style={{ display: "flex", gap: "0.55rem", alignItems: "flex-start" }}>
          <StatusRail state={healthRail(health)} />
          <div>
            <strong>{service.name}</strong>
            <div className={ui.meta}>
              {lifecycleLabel(m, health)} · {m.projects.servicesSourceProbe}
            </div>
          </div>
        </div>
        <p className={`mono ${ui.meta}`} style={{ margin: 0 }}>
          {service.id.slice(0, 8)}…
        </p>
      </div>

      <div className={styles.metaGrid}>
        <div>
          <div className={styles.metaLabel}>{m.projects.servicesLastObserved}</div>
          <div className={styles.metaValue}>
            {latest ? formatWhen(latest.observed_at, locale) : unknown}
          </div>
        </div>
        <div>
          <div className={styles.metaLabel}>{m.projects.servicesReplicas}</div>
          <div className={styles.metaValue}>
            {fmt(m.projects.servicesReplicasValue, {
              ready: formatNullable(latest?.ready_replicas, locale, unknown),
              desired: formatNullable(latest?.desired_replicas, locale, unknown),
            })}
          </div>
        </div>
        <div>
          <div className={styles.metaLabel}>{m.projects.servicesProbeResult}</div>
          <div className={styles.metaValue}>
            {latest?.collection === "unavailable"
              ? m.projects.servicesCollectionUnavailable
              : formatBool(
                  latest?.probe_success,
                  m.projects.servicesProbeOk,
                  m.projects.servicesProbeFail,
                  unknown,
                )}
          </div>
        </div>
        <div>
          <div className={styles.metaLabel}>{m.projects.servicesProbeDuration}</div>
          <div className={styles.metaValue}>
            {formatNullable(latest?.probe_duration_ms, locale, unknown, " ms")}
          </div>
        </div>
      </div>

      <p className={ui.meta} style={{ marginTop: "0.65rem" }}>
        {m.projects.servicesProbeNote}
      </p>

      <section className={styles.releaseBlock} aria-labelledby={releasesId}>
        <h3 className="display" style={{ fontSize: "0.98rem", margin: 0 }} id={releasesId}>
          {m.projects.servicesReleasesTitle}
        </h3>
        <p className={ui.meta} style={{ margin: "0.25rem 0 0" }}>
          {m.projects.servicesReleasesLead}
        </p>
        {releases.length === 0 ? (
          <p className={ui.meta} style={{ margin: "0.35rem 0 0" }}>
            {m.projects.servicesReleasesEmpty}
          </p>
        ) : (
          <ol className={styles.releaseTimeline}>
            {releases.map((release) => (
              <ReleaseObservationItem
                key={release.id}
                release={release}
                locale={locale}
              />
            ))}
          </ol>
        )}
      </section>

      <h3 className="display" style={{ fontSize: "0.98rem", margin: "0.75rem 0 0" }} id={chartId}>
        {m.projects.servicesChartTitle}
      </h3>

      {service.series.length === 0 ? (
        <p className={ui.meta}>{m.projects.servicesNoSamples}</p>
      ) : (
        <>
          <p className={ui.meta} style={{ marginTop: "0.25rem" }}>
            {fmt(m.projects.servicesScaleMax, { max: formatCount(maxDuration, locale) })}
          </p>
          <div className={styles.chartFrame}>
            <div className={styles.scale} aria-hidden="true">
              {scaleTicks.map((tick, i) => (
                <span key={`svc-scale-${tick}-${i}`}>{formatScaleTick(tick, locale)}</span>
              ))}
            </div>
            <div
              className={styles.chart}
              role="img"
              aria-labelledby={chartId}
              aria-describedby={`${chartId}-desc`}
            >
              <div
                className={styles.chartInner}
                style={{
                  minWidth: `max(100%, ${Math.max(service.series.length, 1) * 0.55}rem)`,
                }}
              >
                {service.series.map((sample, index) => (
                  <SampleBar
                    key={sample.id}
                    sample={sample}
                    maxDuration={maxDuration}
                    locale={locale}
                    unknown={unknown}
                    showLabel={shouldShowAxisLabel(index, service.series.length)}
                  />
                ))}
              </div>
            </div>
          </div>
          <p className={styles.rangeNote}>
            {fmt(m.projects.servicesChartRange, {
              from: sampleTimeLabel(service.series[0]!.observed_at, locale),
              to: sampleTimeLabel(service.series[service.series.length - 1]!.observed_at, locale),
            })}
          </p>
          <p id={`${chartId}-desc`} className="sr-only">
            {fmt(m.projects.servicesChartSummary, {
              count: formatCount(service.series.length, locale),
              max: formatCount(maxDuration, locale),
            })}
          </p>

          <details className={styles.tableDetails}>
            <summary>{m.projects.servicesTableToggle}</summary>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className="sr-only">{m.projects.servicesTableCaption}</caption>
                <thead>
                  <tr>
                    <th scope="col">{m.projects.servicesColWhen}</th>
                    <th scope="col">{m.projects.servicesColCollection}</th>
                    <th scope="col">{m.projects.servicesProbeResult}</th>
                    <th scope="col">{m.projects.servicesProbeDuration}</th>
                    <th scope="col">{m.projects.servicesReplicas}</th>
                  </tr>
                </thead>
                <tbody>
                  {[...service.series].reverse().map((sample) => (
                    <tr key={`row-${sample.id}`}>
                      <th scope="row">{sampleTimeLabel(sample.observed_at, locale)} UTC</th>
                      <td>
                        {sample.collection === "ok"
                          ? m.projects.servicesCollectionOk
                          : m.projects.servicesCollectionUnavailable}
                      </td>
                      <td>
                        {formatBool(
                          sample.probe_success,
                          m.projects.servicesProbeOk,
                          m.projects.servicesProbeFail,
                          unknown,
                        )}
                      </td>
                      <td>{formatNullable(sample.probe_duration_ms, locale, unknown, " ms")}</td>
                      <td>
                        {fmt(m.projects.servicesReplicasValue, {
                          ready: formatNullable(sample.ready_replicas, locale, unknown),
                          desired: formatNullable(sample.desired_replicas, locale, unknown),
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </article>
  );
}

function ReleaseObservationItem({
  release,
  locale,
}: {
  release: ServiceReleaseObservation;
  locale: Locale;
}) {
  const { m, fmt } = useLocale();
  const stateLabel = releaseStateLabel(m, release.state);
  const revisionLabel = fmt(m.projects.servicesReleaseRevision, {
    revision: release.revision,
  });
  const when = formatWhen(release.observed_at, locale);

  return (
    <li className={styles.releaseItem}>
      <span
        className={[styles.releaseDot, releaseDotClass(release.state)].filter(Boolean).join(" ")}
        aria-hidden
      />
      <div>
        <div className={styles.releaseRow}>
          <span className={styles.releaseState}>{stateLabel}</span>
          <span className={`mono ${ui.meta}`}>{revisionLabel}</span>
        </div>
        <p className={`mono ${ui.meta} ${styles.releaseWhen}`}>{when}</p>
      </div>
    </li>
  );
}

function SampleBar({
  sample,
  maxDuration,
  locale,
  unknown,
  showLabel,
}: {
  sample: ServiceSample;
  maxDuration: number;
  locale: string;
  unknown: string;
  showLabel: boolean;
}) {
  const { m } = useLocale();
  const fullLabel = sampleTimeLabel(sample.observed_at, locale);
  const axis = sampleAxisLabel(sample.observed_at, locale);
  const missing =
    sample.probe_duration_ms == null || !Number.isFinite(sample.probe_duration_ms);
  const duration = missing ? null : sample.probe_duration_ms;
  const ok = sample.probe_success === true;
  const fail = sample.probe_success === false;
  const pct =
    duration != null && maxDuration > 0 ? Math.min(100, (duration / maxDuration) * 100) : 0;

  return (
    <div className={styles.column}>
      <div className={styles.plot}>
        {missing ? (
          <div
            className={styles.barGap}
            title={`${fullLabel} UTC · ${m.projects.servicesProbeDuration}: ${unknown}`}
            aria-hidden="true"
          />
        ) : (
          <div
            className={[styles.bar, ok ? styles.barOk : "", fail ? styles.barFail : ""]
              .filter(Boolean)
              .join(" ")}
            style={{
              height: `${pct}%`,
              // Non-zero samples must paint a visible rect even when % is tiny.
              minHeight: duration! > 0 ? 3 : 0,
            }}
            title={`${fullLabel} UTC · ${formatCount(duration!, locale)} ms`}
          />
        )}
      </div>
      <div className={styles.tickLabel} aria-hidden={showLabel ? undefined : true}>
        {showLabel ? axis : "\u00a0"}
      </div>
    </div>
  );
}
